
// Enhanced function to extract content from various formats with better fallbacks
export const extractContent = (data: any, key: string, fallback: string = 'Conteúdo não disponível'): string => {
  console.log(`🔍 Extraindo conteúdo para a chave: ${key}`);
  console.log(`📊 Estrutura de dados recebida:`, data);
  
  if (!data) {
    console.log(`❌ Dados não fornecidos para ${key}`);
    return fallback;
  }
  
  // Strategy 1: Direct access
  if (data[key] && typeof data[key] === 'string' && data[key].trim().length > 50) {
    console.log(`✅ Acesso direto bem-sucedido para ${key}`);
    return cleanTextContent(data[key]);
  }
  
  // Strategy 2: Nested access patterns
  const nestedKeys = [
    `detailed_trait_analysis.${key}`,
    `analysis.${key}`,
    `content.${key}`,
    key.replace(/_/g, ' '),
    key.toLowerCase(),
    key.toUpperCase(),
    // Specific patterns for common structures
    `${key}_analysis`,
    `${key}_detailed`,
    `${key}_content`
  ];
  
  for (const nestedKey of nestedKeys) {
    const value = nestedKey.includes('.') 
      ? nestedKey.split('.').reduce((obj, k) => obj?.[k], data)
      : data[nestedKey];
    
    if (value && typeof value === 'string' && value.trim().length > 50) {
      console.log(`✅ Acesso aninhado bem-sucedido para ${nestedKey}`);
      return cleanTextContent(value);
    }
  }
  
  // Strategy 3: Fuzzy matching for similar keys
  const allKeys = Object.keys(data);
  const fuzzyMatches = allKeys.filter(k => 
    k.toLowerCase().includes(key.toLowerCase()) || 
    key.toLowerCase().includes(k.toLowerCase())
  );
  
  for (const fuzzyKey of fuzzyMatches) {
    const value = data[fuzzyKey];
    if (value && typeof value === 'string' && value.trim().length > 50) {
      console.log(`✅ Correspondência difusa bem-sucedida para ${fuzzyKey}`);
      return cleanTextContent(value);
    }
  }
  
  // Strategy 4: Extract from complex nested objects
  const extractFromNested = (obj: any, depth = 0): string | null => {
    if (depth > 3) return null; // Prevent infinite recursion
    
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === 'string' && v.length > 50 && 
          (k.toLowerCase().includes(key.toLowerCase()) || 
           key.toLowerCase().includes(k.toLowerCase()))) {
        return cleanTextContent(v);
      }
      if (typeof v === 'object' && v !== null) {
        const nested = extractFromNested(v, depth + 1);
        if (nested) return nested;
      }
    }
    return null;
  };
  
  const nestedResult = extractFromNested(data);
  if (nestedResult) {
    console.log(`✅ Extração de objeto aninhado bem-sucedida para ${key}`);
    return nestedResult;
  }
  
  console.log(`❌ Nenhum conteúdo encontrado para ${key}, usando fallback`);
  return generateIntelligentFallback(key, data);
};

// Advanced text cleaning function
export const cleanTextContent = (content: string): string => {
  if (!content || typeof content !== 'string') return 'Conteúdo não disponível';
  
  let cleaned = content
    // Remove JSON artifacts
    .replace(/```json\s*\{?/g, '')
    .replace(/```\s*\}?$/g, '')
    .replace(/^{\s*"?[^"]*"?\s*:\s*"?/g, '')
    .replace(/"?\s*\}?\s*$/g, '')
    .replace(/^\s*"[^"]*":\s*"?/g, '')
    .replace(/",?\s*$/g, '')
    // Clean escaped characters
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\')
    // Remove extra quotes and brackets
    .replace(/^["'\s]+|["'\s]+$/g, '')
    .replace(/^\[|\]$/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .replace(/\n\s+/g, '\n')
    .trim();
  
  // If content is still too short or looks like metadata, return placeholder
  if (cleaned.length < 30 || 
      /^(null|undefined|NaN|{}|\[\]|""|'')$/.test(cleaned) ||
      /^[a-zA-Z_]+$/.test(cleaned)) {
    return 'Análise detalhada não disponível nesta seção.';
  }
  
  return cleaned;
};

// Generate intelligent fallback content based on available data
export const generateIntelligentFallback = (key: string, data: any): string => {
  console.log(`🤖 Gerando fallback inteligente para ${key}`);
  
  // Try to extract any meaningful content from the data structure
  const extractAnyContent = (obj: any): string[] => {
    const contents: string[] = [];
    
    const traverse = (item: any, depth = 0) => {
      if (depth > 2) return;
      
      if (typeof item === 'string' && item.length > 30) {
        const cleaned = cleanTextContent(item);
        if (cleaned && cleaned !== 'Conteúdo não disponível' && 
            cleaned !== 'Análise detalhada não disponível nesta seção.') {
          contents.push(cleaned);
        }
      } else if (typeof item === 'object' && item !== null) {
        Object.values(item).forEach(value => traverse(value, depth + 1));
      }
    };
    
    traverse(obj);
    return contents;
  };
  
  const availableContents = extractAnyContent(data);
  
  if (availableContents.length > 0) {
    // Use the first meaningful content found as a basis
    const baseContent = availableContents[0];
    return `Baseado na análise disponível: ${baseContent.substring(0, 200)}...`;
  }
  
  // Fallback descriptions based on section type
  const fallbackDescriptions: Record<string, string> = {
    'executive_summary': 'Esta análise fornece insights detalhados sobre o perfil psicológico baseado nos resultados do teste de personalidade.',
    'openness_deep_dive': 'A abertura à experiência reflete a disposição para explorar novas ideias, criatividade e curiosidade intelectual.',
    'conscientiousness_deep_dive': 'A conscienciosidade manifesta-se através da organização, autodisciplina e orientação para objetivos.',
    'extraversion_deep_dive': 'A extroversão caracteriza-se pela energia direcionada ao mundo externo e interações sociais.',
    'agreeableness_deep_dive': 'A amabilidade reflete a tendência para ser cooperativo, confiante e empático nas relações.',
    'neuroticism_deep_dive': 'O neuroticismo indica a tendência para experienciar emoções negativas e instabilidade emocional.',
    'psychological_architecture': 'A arquitetura psicológica descreve como os diferentes aspectos da personalidade se organizam.',
    'interpersonal_dynamics_analysis': 'As dinâmicas interpessoais revelam como a personalidade influencia as relações.',
    'behavioral_manifestations': 'As manifestações comportamentais mostram como os traços se expressam no cotidiano.',
    'cognitive_processing_style': 'O estilo cognitivo descreve as preferências para processar informações.',
    'emotional_regulation_system': 'O sistema de regulação emocional indica como as emoções são gerenciadas.',
    'defense_mechanisms_analysis': 'Os mecanismos de defesa representam estratégias para lidar com conflitos.',
    'psychological_vulnerabilities': 'As vulnerabilidades identificam áreas de potencial fragilidade emocional.',
    'psychological_strengths_resources': 'As forças destacam capacidades e características adaptativas.',
    'neurobiological_correlates': 'Os correlatos neurobiológicos exploram as bases cerebrais dos padrões.',
    'developmental_perspective': 'A perspectiva desenvolvimental analisa a evolução dos traços ao longo da vida.',
    'therapeutic_recommendations_detailed': 'As recomendações fornecem diretrizes para crescimento e bem-estar.',
    'psychoanalytic_insights_expanded': 'Os insights oferecem compreensão dos processos inconscientes.',
    'integration_synthesis_comprehensive': 'A síntese combina todos os aspectos em uma compreensão holística.',
    'actionable_insights_practical': 'Os insights práticos traduzem a análise em estratégias concretas.'
  };
  
  return fallbackDescriptions[key] || 'Análise personalizada baseada nos seus resultados de personalidade.';
};
